import { Typography } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { T, TPlainTextKey } from '../../../../../../Translations';
import { AccessLevel, ALL_ACCESS_LEVELS } from '../Selectors';

interface Props {
  counts: Record<AccessLevel, number>;
  active: AccessLevel;
  onSelect: (level: AccessLevel) => void;
}

export const tabLabel = (level: AccessLevel): TPlainTextKey => {
  switch (level) {
    case 'full':
      return 'Fully licensed';
    case 'partial':
      return 'Partially licensed';
    case 'none':
      return 'Not licensed';
    default:
      return assertNever(level);
  }
};

// Every tab is reachable, so every tab needs something to say when empty.
export const emptyTabMessage = (level: AccessLevel): TPlainTextKey => {
  switch (level) {
    case 'full':
      return 'No one has full access yet.';
    case 'partial':
      return 'Everyone is either fully licensed or not licensed at all.';
    case 'none':
      return 'Everyone holds a license.';
    default:
      return assertNever(level);
  }
};

// Same underline tab pattern as the participant search on this screen, with the
// palette taken from tokens rather than the literal teal/gray it hardcodes.
function AccessTabs({ counts, active, onSelect }: Props) {
  return (
    <div className="border-b border-border">
      <div className="flex gap-6">
        {ALL_ACCESS_LEVELS.map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => onSelect(level)}
            aria-selected={level === active}
            className={clsx(
              'border-b-2 pb-2 transition-colors',
              level === active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            data-intercom-target={`share-license-access-tab-${level}`}
          >
            <Typography
              variant="bodyMedium"
              as="span"
              fontWeight={level === active ? 'semibold' : 'normal'}
            >
              <T kind={tabLabel(level)} /> ({counts[level]})
            </Typography>
          </button>
        ))}
      </div>
    </div>
  );
}

export default AccessTabs;
